import React, { useEffect, useRef } from "react";
import { team } from "./constants";
import { Layout } from "../components/Layout";
import { RevSlider2 } from "./RevSlider2";
import { AfterSlider } from "./AfterSlider";
import { Home2ndOpinion } from "./Home2ndOpinion";
import TeamWithCarousel from "../components/TeamWithCarousel";

/* ─── Animated section reveal hook ─── */
function useReveal(threshold = 0.15) {
	const ref = useRef(null);
	useEffect(() => {
		if (!ref.current) return;
		const el = ref.current;
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					el.style.opacity = "1";
					el.style.transform = "translateY(0)";
					observer.disconnect();
				}
			},
			{ threshold }
		);
		observer.observe(el);
		return () => observer.disconnect();
	}, [threshold]);
	return ref;
}

/* ─── Main Home Component ─── */
export const Home = ({ match }) => {
	const aboutRef = useReveal();
	const infoRef = useReveal();

	return (
		<Layout>
			<div>
				<RevSlider2 />
				<AfterSlider />

				{/* ── About / Intro Section ── */}
				<section
					className="section-modern"
					style={{
						background: "linear-gradient(180deg, #ffffff 0%, #f0f9f9 100%)",
						position: "relative",
						overflow: "hidden",
					}}
				>
					{/* Decorative blobs */}
					<div
						style={{
							position: "absolute",
							width: 400,
							height: 400,
							background: "rgba(0,184,184,0.08)",
							borderRadius: "50%",
							filter: "blur(70px)",
							top: -100,
							right: -100,
							pointerEvents: "none",
						}}
					/>
					<div
						style={{
							position: "absolute",
							width: 300,
							height: 300,
							background: "rgba(55,81,75,0.07)",
							borderRadius: "50%",
							filter: "blur(60px)",
							bottom: -60,
							left: -80,
							pointerEvents: "none",
						}}
					/>

					<div className="container" style={{ position: "relative", zIndex: 1 }}>
						<div className="row align-items-center">
							{/* Image side */}
							<div className="col-12 col-lg-5 mb-5 mb-lg-0">
								<div
									ref={aboutRef}
									style={{
										opacity: 0,
										transform: "translateY(40px)",
										transition: "opacity 0.8s ease, transform 0.8s ease",
										position: "relative",
									}}
								>
									{/* Glow behind image */}
									<div
										style={{
											position: "absolute",
											inset: -30,
											background:
												"radial-gradient(ellipse, rgba(0,184,184,0.2) 0%, transparent 70%)",
											borderRadius: 30,
											zIndex: 0,
											animation: "pulse-glow 3s ease-in-out infinite",
										}}
									/>
									<div
										className="img-hover-zoom"
										style={{
											borderRadius: 24,
											boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
											position: "relative",
											zIndex: 1,
											overflow: "hidden",
										}}
									>
										<img
											src="https://images.pexels.com/photos/6457556/pexels-photo-6457556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
											alt="24Telemed doctors"
											style={{ width: "100%", height: 420, objectFit: "cover" }}
										/>
										{/* Overlay badge */}
										<div
											style={{
												position: "absolute",
												bottom: 24,
												left: 24,
												background: "rgba(255,255,255,0.95)",
												backdropFilter: "blur(10px)",
												borderRadius: 16,
												padding: "1rem 1.5rem",
												display: "flex",
												alignItems: "center",
												gap: 12,
												boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
											}}
										>
											<div
												style={{
													width: 44,
													height: 44,
													borderRadius: 12,
													background:
														"linear-gradient(135deg, #37514B 0%, #00b8b8 100%)",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													color: "#fff",
													fontSize: "1.2rem",
													flexShrink: 0,
												}}
											>
												<i className="fas fa-heartbeat" />
											</div>
											<div>
												<div
													style={{
														fontWeight: 700,
														fontSize: "0.9rem",
														color: "#1a2332",
													}}
												>
													24/7 Healthcare
												</div>
												<div style={{ fontSize: "0.78rem", color: "#6b7280" }}>
													Always here for you
												</div>
											</div>
										</div>
									</div>

									{/* Floating badge top-right */}
									<div
										style={{
											position: "absolute",
											top: -20,
											right: -20,
											background:
												"linear-gradient(135deg, #37514B 0%, #00b8b8 100%)",
											borderRadius: "50%",
											width: 90,
											height: 90,
											display: "flex",
											flexDirection: "column",
											alignItems: "center",
											justifyContent: "center",
											boxShadow: "0 8px 25px rgba(0,184,184,0.4)",
											color: "#fff",
											zIndex: 2,
											animation: "floatSlow 5s ease-in-out infinite",
										}}
									>
										<span style={{ fontWeight: 800, fontSize: "1.3rem", lineHeight: 1 }}>
											10K+
										</span>
										<span style={{ fontSize: "0.65rem", opacity: 0.9, textAlign: "center" }}>
											Patients
										</span>
									</div>
								</div>
							</div>

							{/* Text side */}
							<div className="col-12 col-lg-6 offset-lg-1">
								<div
									ref={infoRef}
									style={{
										opacity: 0,
										transform: "translateY(40px)",
										transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
									}}
								>
									{/* Tag */}
									<div className="section-tag" style={{ marginBottom: "1rem" }}>
										About 24Telemed
									</div>

									{/* Accent line */}
									<div className="accent-line" />

									<h2
										className="section-title-modern"
										style={{ marginBottom: "1.25rem" }}
									>
										Secure, Simple &amp;{" "}
										<span>Affordable Healthcare</span>
									</h2>

									<p
										style={{
											color: "#4b5563",
											lineHeight: 1.8,
											fontSize: "1rem",
											marginBottom: "1.25rem",
										}}
									>
										24Telemed is a secure, simple, and affordable electronic health
										service created to save lives, give you peace of mind, and
										provide care that fits your lifestyle. Connect with qualified
										and experienced doctors 24/7, wherever you are.
									</p>
									<p
										style={{
											color: "#4b5563",
											lineHeight: 1.8,
											fontSize: "1rem",
											marginBottom: "2rem",
										}}
									>
										Residents of Nigeria can access healthcare services via phone
										call through dedicated lines — with qualified doctors available
										to help manage your medical conditions anytime, anywhere.
									</p>

									{/* Feature list */}
									<div style={{ marginBottom: "2rem" }}>
										{[
											{ icon: "fas fa-phone-alt", text: "24/7 Doctors on Call" },
											{
												icon: "fas fa-video",
												text: "eConsult Video Consultations",
											},
											{
												icon: "fas fa-user-md",
												text: "2nd Medical Opinion from Specialists",
											},
										].map((item, i) => (
											<div
												key={i}
												className="check-item"
												style={{ animationDelay: `${i * 0.15}s` }}
											>
												<div className="check-icon">
													<i className={item.icon} />
												</div>
												<span style={{ fontWeight: 500, color: "#374151" }}>
													{item.text}
												</span>
											</div>
										))}
									</div>

									<a href="/about" className="btn-modern-primary">
										<span>Learn More About Us</span>
										<i className="fas fa-arrow-right" />
									</a>
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* ── "How We Help" Section ── */}
				<WhatWeDo />

				{/* ── 2nd Opinion & Appointment ── */}
				<Home2ndOpinion />

				</div>
		</Layout>
	);
};

export const OurTeam = ({ bg, partner, count }) => {
	return <TeamWithCarousel title="Meet Our Team" team={team} />;
};

/* ─── WhatWeDo ─── */
export const WhatWeDo = () => {
	const leftRef = useReveal();
	const rightRef = useReveal();

	const helps = [
		{
			icon: "fas fa-search",
			title: "Clarity",
			text: "We help you understand and clarify your medical problems.",
			color: "#37514B",
		},
		{
			icon: "fas fa-stethoscope",
			title: "Alternative Diagnosis",
			text: "We offer you alternative diagnosis and treatment options.",
			color: "#00b8b8",
		},
		{
			icon: "fas fa-globe",
			title: "Home & Abroad",
			text: "We connect you with specialists around the globe.",
			color: "#37514B",
		},
		{
			icon: "fas fa-lightbulb",
			title: "Best Solutions",
			text: "We offer the best treatment options for your medical conditions.",
			color: "#00b8b8",
		},
	];

	return (
		<section
			style={{
				padding: "6rem 0",
				background: "#0d1f1a",
				position: "relative",
				overflow: "hidden",
			}}
		>
			{/* Background decoration */}
			<div
				style={{
					position: "absolute",
					inset: 0,
					backgroundImage:
						"radial-gradient(rgba(0,184,184,0.08) 1px, transparent 1px)",
					backgroundSize: "32px 32px",
					pointerEvents: "none",
				}}
			/>
			<div
				style={{
					position: "absolute",
					top: "50%",
					left: "30%",
					width: 600,
					height: 600,
					background: "rgba(0,184,184,0.06)",
					borderRadius: "50%",
					filter: "blur(80px)",
					transform: "translate(-50%, -50%)",
					pointerEvents: "none",
				}}
			/>

			<div className="container" style={{ position: "relative", zIndex: 1 }}>
				<div className="row align-items-center g-5">
					{/* Left: Image + CTA */}
					<div className="col-12 col-lg-5">
						<div
							ref={leftRef}
							style={{
								opacity: 0,
								transform: "translateY(40px)",
								transition: "opacity 0.8s ease, transform 0.8s ease",
							}}
						>
							{/* Contact CTA card */}
							<div
								style={{
									background:
										"linear-gradient(135deg, #37514B 0%, #00b8b8 100%)",
									borderRadius: 24,
									padding: "2.5rem",
									marginBottom: "1.5rem",
									position: "relative",
									overflow: "hidden",
								}}
							>
								<div
									style={{
										position: "absolute",
										top: -40,
										right: -40,
										width: 160,
										height: 160,
										background: "rgba(255,255,255,0.08)",
										borderRadius: "50%",
									}}
								/>
								<i
									className="ti-quote-left"
									style={{
										fontSize: "2rem",
										color: "rgba(255,255,255,0.6)",
										marginBottom: "1rem",
										display: "block",
									}}
								/>
								<h5
									style={{
										color: "#fff",
										fontWeight: 600,
										marginBottom: "1.5rem",
										lineHeight: 1.5,
									}}
								>
									Contact us today to put your mind at ease concerning your
									medical conditions.
								</h5>
								<a
									href="/contact"
									style={{
										display: "inline-flex",
										alignItems: "center",
										gap: 8,
										background: "rgba(255,255,255,0.15)",
										backdropFilter: "blur(10px)",
										border: "1px solid rgba(255,255,255,0.3)",
										color: "#fff",
										padding: "0.75rem 1.5rem",
										borderRadius: 50,
										fontWeight: 600,
										textDecoration: "none",
										transition: "all 0.3s ease",
										fontSize: "0.9rem",
									}}
									onMouseEnter={(e) => {
										e.currentTarget.style.background = "rgba(255,255,255,0.25)";
										e.currentTarget.style.transform = "translateX(6px)";
									}}
									onMouseLeave={(e) => {
										e.currentTarget.style.background = "rgba(255,255,255,0.15)";
										e.currentTarget.style.transform = "translateX(0)";
									}}
								>
									Contact us now
									<i className="feather icon-feather-arrow-right" />
								</a>
							</div>

							{/* Image */}
							<div
								className="img-hover-zoom"
								style={{
									borderRadius: 20,
									overflow: "hidden",
									boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
									height: 300,
								}}
							>
								<img
									src="https://images.pexels.com/photos/6457556/pexels-photo-6457556.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=750&w=1260"
									alt="Healthcare"
									style={{ width: "100%", height: "100%", objectFit: "cover" }}
								/>
							</div>
						</div>
					</div>

					{/* Right: How we help */}
					<div className="col-12 col-lg-6 offset-lg-1">
						<div
							ref={rightRef}
							style={{
								opacity: 0,
								transform: "translateY(40px)",
								transition: "opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s",
							}}
						>
							<div className="section-tag" style={{ color: "#00b8b8", WebkitTextFillColor: "#00b8b8" }}>
								Our Approach
							</div>
							<div
								style={{
									width: 60,
									height: 4,
									background:
										"linear-gradient(90deg, #37514B 0%, #00b8b8 100%)",
									borderRadius: 2,
									margin: "1rem 0 1.5rem",
								}}
							/>
							<h3
								style={{
									color: "#fff",
									fontWeight: 700,
									marginBottom: "2.5rem",
									fontSize: "clamp(1.6rem, 3vw, 2.2rem)",
									lineHeight: 1.3,
								}}
							>
								How we help our patients
							</h3>

							<div className="row">
								{helps.map((item, i) => (
									<div key={i} className="col-12 col-sm-6" style={{ marginBottom: '1rem' }}>
										<div
											style={{
												background: "rgba(255,255,255,0.05)",
												border: "1px solid rgba(255,255,255,0.1)",
												borderRadius: 16,
												padding: "1.5rem",
												transition: "all 0.4s ease",
												cursor: "default",
												animationDelay: `${i * 0.1}s`,
											}}
											onMouseEnter={(e) => {
												e.currentTarget.style.background =
													"rgba(0,184,184,0.12)";
												e.currentTarget.style.borderColor =
													"rgba(0,184,184,0.4)";
												e.currentTarget.style.transform = "translateY(-6px)";
											}}
											onMouseLeave={(e) => {
												e.currentTarget.style.background =
													"rgba(255,255,255,0.05)";
												e.currentTarget.style.borderColor =
													"rgba(255,255,255,0.1)";
												e.currentTarget.style.transform = "translateY(0)";
											}}
										>
											<div
												style={{
													width: 42,
													height: 42,
													borderRadius: 10,
													background:
														i % 2 === 0
															? "rgba(55,81,75,0.3)"
															: "rgba(0,184,184,0.2)",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													marginBottom: "1rem",
													color: item.color,
													fontSize: "1.1rem",
												}}
											>
												<i className={item.icon} />
											</div>
											<div
												style={{
													fontWeight: 700,
													color: "#fff",
													marginBottom: "0.4rem",
													fontSize: "0.95rem",
												}}
											>
												{item.title}
											</div>
											<p
												style={{
													color: "rgba(255,255,255,0.6)",
													fontSize: "0.85rem",
													lineHeight: 1.6,
													margin: 0,
												}}
											>
												{item.text}
											</p>
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};
